import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useBillingHistory } from '../../hooks/useAccount';

const STATUS_COLORS: Record<string, 'success' | 'warning' | 'info' | 'default'> = {
  PAID: 'success',
  PENDING: 'warning',
  REFUNDED: 'info',
  FAILED: 'default',
};

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(dateStr: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(dateStr));
}

export default function BillingHistoryPage() {
  const { data: records, isLoading, isError, error } = useBillingHistory();

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width="40%" height={48} />
        <Skeleton variant="rectangular" height={100} sx={{ mt: 2, borderRadius: 2 }} />
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rectangular" height={80} sx={{ mt: 2, borderRadius: 2 }} />
        ))}
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error">
        {(error as Error)?.message ?? 'Failed to load billing history'}
      </Alert>
    );
  }

  if (!records) return null;

  const totalSpentCents = records.reduce((sum, r) => sum + r.totalCents, 0);
  const paidCount = records.filter((r) => r.status === 'PAID').length;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <ReceiptLongIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4" fontWeight={700}>
          Billing History
        </Typography>
      </Stack>

      {/* Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            justifyContent="space-around"
            textAlign="center"
          >
            <Box>
              <Typography variant="h4" fontWeight={700} color="primary.main">
                {formatCents(totalSpentCents)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Spent
              </Typography>
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={700}>
                {records.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Invoices
              </Typography>
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={700} color="success.main">
                {paidCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Paid
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Billing Records */}
      {records.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" textAlign="center" py={3}>
              No billing records yet. Charges will appear here once you create events with guests.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {records.map((record) => (
            <Card key={record.id}>
              <CardContent>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ sm: 'center' }}
                  spacing={1}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {record.eventTitle}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(record.eventDate)} &middot; {record.guestCount} guest{record.guestCount !== 1 ? 's' : ''}
                    </Typography>
                  </Box>

                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="h6" fontWeight={700}>
                      {formatCents(record.totalCents)}
                    </Typography>
                    <Chip
                      label={record.status}
                      color={STATUS_COLORS[record.status] ?? 'default'}
                      size="small"
                    />
                  </Stack>
                </Stack>

                {/* Breakdown */}
                <Stack
                  direction="row"
                  spacing={2}
                  mt={1}
                  flexWrap="wrap"
                  sx={{ '& > *': { minWidth: 'fit-content' } }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Base: {formatCents(record.basePriceCents)}
                  </Typography>
                  {record.overageGuestCents > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      Guest overage: {formatCents(record.overageGuestCents)}
                    </Typography>
                  )}
                  {record.emailOverageCount > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      Email overage ({record.emailOverageCount}): {formatCents(record.emailOverageCents)}
                    </Typography>
                  )}
                  {record.paidAt && (
                    <Typography variant="caption" color="text.secondary">
                      Paid: {formatDate(record.paidAt)}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
}
